import type { Environment } from "./chains.ts";

/**
 * Each deployment's WebAuthn relying-party ID (ADR-0010 decision 2).
 *
 * Committed, not read from the environment, and that is the whole decision
 * (#158). A passkey is bound to its RP ID forever: change it and every
 * credential minted under the old one is dead, along with the Profile it
 * signs for. That makes this the one setting in the product that cannot be
 * undone, so it lives in reviewed code beside the chain profile rather than in
 * a `.env` somebody types on a host.
 *
 * Production's is the apex, `ancient.consulting`, not the host it is served
 * from, so that the product can move between subdomains of it without
 * orphaning a single credential. Staging's is its own host, so that no staging
 * credential can ever be presented to production or the reverse.
 *
 * Not in `CHAIN_PROFILES`: an RP ID is a fact about *our* deployment, not
 * about the chain, and anyone else deploying against Base Sepolia would have a
 * different one. It is committed anyway because, unlike the product wallet,
 * getting it wrong cannot be fixed by changing it.
 */
export const RP_IDS: Readonly<Record<Environment, string>> = {
  staging: "staging.karvstokk.ancient.consulting",
  production: "ancient.consulting",
};

/**
 * Whether a page served from `host` may claim `rpId`.
 *
 * WebAuthn's rule: the RP ID must be the origin's effective domain or a
 * registrable domain suffix of it. So `karvstokk.ancient.consulting` may claim
 * `ancient.consulting`, and `ancient.consulting.example` may not — the suffix
 * has to start at a label boundary, which is what the leading dot is for. The
 * public-suffix half of the rule (no claiming `consulting`) is not checked:
 * both RP IDs are committed above, and neither is a public suffix.
 *
 * Compared as given: the caller passes a `new URL` hostname, which is already
 * lower-case, and both RP IDs are committed lower-case.
 */
export function hostCoversRpId(host: string, rpId: string): boolean {
  return host === rpId || host.endsWith(`.${rpId}`);
}
